import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Cart, CheckPaymentStatusResponse, OrderFromCart, ShipmentMethod } from 'src/app/interfaces';
import { CartService } from 'src/app/services';
import {
    AddCouponToCart,
    AddToCart,
    CreateCartOrder,
    GetCart,
    GetCartOrderState,
    RemoveCouponToCart,
    RemoveFromCart,
    ShipmentMethods,
    UpdateCartOrder
} from './cart.actions';

export interface CartStateModel {
    cart: Cart;
    shipmentMethods: ShipmentMethod[];
    order: OrderFromCart;
    orderStatus: CheckPaymentStatusResponse;
}

@State<CartStateModel>({
    name: 'cart',
    defaults: {
        cart: null,
        order: null,
        orderStatus: null,
        shipmentMethods: []
    }
})
@Injectable()
export class CartState {
    constructor(private service: CartService) {}

    @Selector()
    static counter(state: CartStateModel) {
        return state.cart?.products?.length;
    }

    @Selector()
    static cart(state: CartStateModel) {
        return state.cart;
    }
    @Selector()
    static subTotal(state: CartStateModel) {
        return state.cart?.subTotal;
    }
    @Selector()
    static coupons(state: CartStateModel) {
        return state.cart?.couponsToApply;
    }

    @Selector()
    static shipmentOptions(state: CartStateModel) {
        return state.shipmentMethods;
    }

    @Selector()
    static orderFromCart(state: CartStateModel) {
        return state.order;
    }

    @Selector()
    static orderStatus(state: CartStateModel) {
        return state.orderStatus;
    }

    @Action(AddToCart)
    add(ctx: StateContext<CartStateModel>, { payload }: AddToCart) {
        return this.service.addToCart(payload).pipe(
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(RemoveFromCart)
    remove(ctx: StateContext<CartStateModel>, { payload }: RemoveFromCart) {
        return this.service.removeFromCart(payload).pipe(
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(GetCart)
    get(ctx: StateContext<CartStateModel>) {
        return this.service.getCart().pipe(
            catchError((err) => of(null)),
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(ShipmentMethods)
    getShipmentOptions(ctx: StateContext<CartStateModel>) {
        return this.service.getShipmentOptions().pipe(
            catchError((err) => of([])),
            tap((res) => {
                ctx.patchState({
                    shipmentMethods: res
                });
            })
        );
    }

    @Action(UpdateCartOrder)
    updateOrder(ctx: StateContext<CartStateModel>, { payload }: UpdateCartOrder) {
        ctx.patchState({
            order: payload
        });
    }

    @Action(CreateCartOrder)
    createCartOrder(ctx: StateContext<CartStateModel>) {
        return this.service.createOrderFromCart(ctx.getState().order).pipe(
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(AddCouponToCart)
    addCoupon(ctx: StateContext<CartStateModel>, { payload }: AddCouponToCart) {
        return this.service.addCouponToCart(payload).pipe(
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(RemoveCouponToCart)
    removeCoupon(ctx: StateContext<CartStateModel>, { payload }: RemoveCouponToCart) {
        return this.service.removeCouponToCart(payload).pipe(
            tap((res) => {
                ctx.patchState({
                    cart: res
                });
            })
        );
    }

    @Action(GetCartOrderState)
    getCartOrderState(ctx: StateContext<CartStateModel>) {
        return this.service.getCartOrderStatus().pipe(
            tap((res) => {
                ctx.patchState({
                    orderStatus: res
                });
            })
        );
    }
}
